import { Alert, Modal, Form } from "react-bootstrap";
import { Input } from "./Input";
import { Select } from "./Select";
import { Controller, useForm } from "react-hook-form";
import { Button } from "./Button";
import { HeaderText } from "./HeaderText";
import { colors } from "../Data/Common";
import { Vehicle } from "../Data/ApiTypes";
import { getStateEnumFromCode, UsState, showStateCode } from "../Data/UsState";
import { useEffect, useState } from "react";
import { CarFront, ChevronDown } from "react-bootstrap-icons";

function sanitizeLicensePlate( value: string ): string {
    return value.replace( /[^a-zA-Z0-9]/g, "" ).toUpperCase();
}

export interface AddVehicleFormProps {
    onSubmit: ( v: Vehicle ) => void;
    onCancel: () => void;
    isCheckout?: boolean;
    submitText?: string;
    cancelText?: string;
    autoFocus?: boolean;
}

export function AddVehicleForm( {
    onSubmit,
    onCancel,
    isCheckout = false,
    submitText = "Add Vehicle",
    cancelText = "Cancel",
    autoFocus = false,
}: AddVehicleFormProps ) {
    const [showDetails, setShowDetails] = useState( false );
    const { handleSubmit, register, getValues, reset, control, formState: { errors }, setFocus }
        = useForm<Vehicle>( {
            defaultValues: {
                licensePlateNumber: "",
                stateCode: "",
                make: isCheckout ? "N/A" : "",
                model: isCheckout ? "N/A" : "",
                color: isCheckout ? "N/A" : undefined
            }
        } );

    useEffect(() => {
        if( autoFocus )
            setFocus( "licensePlateNumber" );
    }, [autoFocus, setFocus]);

    function clear() {
        setShowDetails( false );
        reset( {
            make: isCheckout ? "N/A" : "",
            model: isCheckout ? "N/A" : "",
            licensePlateNumber: "",
            stateCode: "",
            color: isCheckout ? "N/A" : undefined
        } );
    }

    function submit() {
        const vals = getValues();
        if( !isCheckout && !showDetails ) {
            vals.make = "N/A";
            vals.model = "N/A";
            vals.color = "N/A";
        }
        onSubmit( vals );
        clear();
    }

    return <Form onSubmit={handleSubmit( submit )}>
        {isCheckout && (
            <Alert variant="info" className="mb-3">
                Please ensure your license plate and state are correct. <b> Incorrect information may result in a parking citation or tow.</b>
            </Alert>
        )}
        <div className="d-flex gap-2">
            <div className="flex-grow-1">
                <Controller
                    control={control}
                    name="licensePlateNumber"
                    rules={{ required: "License Plate Number is required" }}
                    render={( { field } ) => (
                        <Input
                            type="text"
                            label="License Plate Number"
                            placeholder="example: ABC000"
                            formNoValidate
                            maxLength={10}
                            error={errors.licensePlateNumber}
                            autoCapitalize="characters"
                            autoCorrect="off"
                            spellCheck={false}
                            {...field}
                            value={field.value ?? ""}
                            onChange={( e ) => {
                                field.onChange( sanitizeLicensePlate( e.target.value ) );
                            }}
                        />
                    )}
                />
            </div>
            <div style={{ width: "120px" }}>
                <Select
                    label="State"
                    {...register( "stateCode", {
                        required: "State is required",
                        validate: ( value ) => {
                            if( !value )
                                return true;
                            const stateEnum = getStateEnumFromCode( value );
                            return stateEnum !== undefined || "Invalid state code";
                        },
                    } )}
                    error={errors.stateCode}>
                    <option value="">Select</option>
                    {Object.values(UsState)
                        .filter(value => typeof value === 'number')
                        .map(stateEnum => (
                            <option key={stateEnum} value={showStateCode(stateEnum as UsState)}>
                                {showStateCode(stateEnum as UsState)}
                            </option>
                        ))}
                </Select>
            </div>
        </div>
        {!isCheckout && (
            <div className="rounded-4 border mb-3" style={{ background: "#FAFAFA", borderColor: "#ECECEC" }}>
                <button type="button"
                        className="w-100 d-flex align-items-center gap-2 border-0 bg-transparent text-start px-3 py-2"
                        onClick={() => setShowDetails( open => !open )}>
                    <CarFront size={18} className="text-secondary flex-shrink-0" />
                    <span className="flex-grow-1 min-w-0">
                        <span className="d-block fw-semibold">Vehicle details</span>
                        {!showDetails && <span className="d-block text-secondary small">Make, model and color</span>}
                    </span>
                    <span className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1 small text-secondary flex-shrink-0"
                          style={{ background: "#F0F0F0" }}>
                        Optional
                        <ChevronDown size={12} style={{ transform: showDetails ? "rotate(180deg)" : undefined }} />
                    </span>
                </button>
                {showDetails && (
                    <div className="px-3 pb-3">
                        <div className="d-flex flex-row w-100 gap-3 mb-3">
                            <Input className="w-100"
                                type="text"
                                label="Make"
                                placeholder="example: Kia"
                                formNoValidate
                                {...register( "make", { required: "Make is required" } )}
                                error={errors.make} />
                            <Input className="w-100"
                                type="text"
                                label="Model"
                                placeholder="example: Sorento"
                                formNoValidate
                                {...register( "model", { required: "Model is required" } )}
                                error={errors.model} />
                        </div>
                        <Controller
                            control={control}
                            name="color"
                            rules={{ required: "Vehicle color is required" }}
                            render={( { field: { onChange, value } } ) => (
                                <div className="mb-1">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span>Color</span>
                                        <span className="text-secondary small">{value ? value : "None selected"}</span>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {colors.map( col => {
                                            const selected = value === col;
                                            return <button key={col}
                                                type="button"
                                                aria-label={col}
                                                className="p-0"
                                                style={{
                                                    width: 28,
                                                    height: 28,
                                                    borderRadius: "50%",
                                                    backgroundColor: col,
                                                    border: col === "White" ? "1px solid #D0D0D0" : "1px solid transparent",
                                                    boxShadow: selected ? "0 0 0 2px #fff, 0 0 0 4px #212529" : undefined,
                                                }}
                                                onClick={() => onChange( col )} />;
                                        } )}
                                    </div>
                                    {errors.color && <Form.Text className="d-block text-danger mt-2">{errors.color.message}</Form.Text>}
                                </div>
                            )} />
                    </div>
                )}
            </div>
        )}
        <div className="d-flex justify-content-end gap-2 mt-3">
            <Button type="button" className="w-100" onClick={() => { clear(); onCancel(); }}>
                {cancelText}
            </Button>
            <Button type="submit" className="w-100 btn-main">
                {submitText}
            </Button>
        </div>
    </Form>;
}

export interface AddVehicleModalProps {
    show:     boolean;
    setShow:  ( b: boolean ) => void;
    onSubmit: ( v: Vehicle ) => void;
    isCheckout?: boolean;
    /** Set false when opened over another modal to avoid a double mask. */
    backdrop?: boolean | "static";
}

export function AddVehicleModal( props: AddVehicleModalProps ) {
    const { show, setShow, onSubmit, isCheckout = false, backdrop = true } = { ...props };

    return <Modal show={props.show} centered backdrop={backdrop}>
            <Modal.Header>
                <HeaderText>Add Vehicle</HeaderText>
            </Modal.Header>
            <Modal.Body>
                <AddVehicleForm
                    isCheckout={isCheckout}
                    autoFocus={show}
                    onSubmit={ v => { onSubmit( v ); setShow( false ); } }
                    onCancel={() => setShow( false )}
                />
            </Modal.Body>
        </Modal>
}